/**
 * QuoteItemsEditor — admin items table for `QuoteDetail.tsx` (P16-T10
 * step 4). Composition over `Table` + `Input`.
 *
 * The component is fully controlled — the parent (`QuoteDetail`) owns
 * the items array as RHF form state and passes both the current rows
 * and an `onChange` callback that receives the next array. The editor
 * itself is "dumb": no fetching, no submit, just edits.
 *
 * Editable: quantity, quoted_unit_price_minor (free price text), and a
 * Remove row button. Per row also: an Add row button at the bottom that
 * appends a blank row (variant_id starts blank; admin types the variant
 * UUID inline — search-select TBD when the Combobox primitive lands).
 *
 * Read-only display: variant_id, requested_unit_price (system price),
 * VAT rate label, live line totals.
 *
 * Live totals: per-row gross + cumulative totals computed via
 * `computeLineTotal` (the same pure helper unit-tested in
 * `quote-validation.test.ts`).
 *
 * Disabled state: when `disabled` is true (terminal quote states +
 * pending submit), inputs render read-only. Add/Remove are suppressed.
 */
import {
  Button,
  Input,
  Numeric,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@wmart/ui";
import { formatGBP } from "../../lib/format.js";
import {
  computeLineTotal,
  type ReviseFormItem,
} from "../../lib/quote-validation.js";

// ---------------------------------------------------------------------------
// Row contract: includes the read-only display fields that the parent
// has from the loaded QuoteDetail. The form-state subset is mutable.
// ---------------------------------------------------------------------------

export interface QuoteEditorRow extends ReviseFormItem {
  /** From the loaded QuoteDetail — does NOT mutate. */
  requested_unit_price_minor: number;
  /** From the loaded QuoteDetail — does NOT mutate (snapshot semantics). */
  vat_category_code: string;
  vat_rate_basis_points: number;
  /**
   * Product/variant identity for display (resolved server-side). Null on
   * brand-new rows the admin is still typing, or when the variant was deleted.
   */
  product_id?: string | null;
  product_title?: string | null;
  variant_title?: string | null;
  sku?: string | null;
}

export interface QuoteItemsEditorProps {
  rows: QuoteEditorRow[];
  onChange: (next: QuoteEditorRow[]) => void;
  disabled?: boolean;
}

/**
 * Format the VAT rate as a human-readable percentage. 2000 bp → "20.00%".
 */
function formatVatRate(basisPoints: number): string {
  return `${(basisPoints / 100).toFixed(2)}%`;
}

/**
 * Compute the effective unit price for a single row. Empty quoted →
 * requested; any non-empty quoted parses to the integer override.
 * Non-numeric quoted falls back to requested (the form-level validator
 * surfaces the parse error separately).
 */
function effectiveUnitPriceMinor(row: QuoteEditorRow): number {
  const raw = row.quoted_unit_price_minor?.trim() ?? "";
  if (raw === "") return row.requested_unit_price_minor;
  const n = Number(raw);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
    return row.requested_unit_price_minor;
  }
  return n;
}

/**
 * Parse a row's quantity for total computation. Falls back to 0 for
 * invalid input — the form-level validator surfaces the parse error
 * separately, so we just want a stable preview number here.
 */
function parsedQty(row: QuoteEditorRow): number {
  const raw = row.quantity?.trim() ?? "";
  if (raw === "") return 0;
  const n = Number(raw);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) return 0;
  return n;
}

export function QuoteItemsEditor({
  rows,
  onChange,
  disabled = false,
}: QuoteItemsEditorProps) {
  function updateRow(idx: number, patch: Partial<QuoteEditorRow>) {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  }

  function removeRow(idx: number) {
    onChange(rows.filter((_, i) => i !== idx));
  }

  function addRow() {
    onChange([
      ...rows,
      {
        variant_id: "",
        quantity: "1",
        quoted_unit_price_minor: "",
        // Defaults for the read-only fields — admin will set on the row;
        // server re-resolves at revise time so these placeholders never
        // make it to the wire.
        requested_unit_price_minor: 0,
        vat_category_code: "",
        vat_rate_basis_points: 0,
      },
    ]);
  }

  // ---- Totals: pre-compute once per render ----
  let netTotal = 0;
  let vatTotal = 0;
  const rowTotals = rows.map((r) => {
    const lt = computeLineTotal(
      effectiveUnitPriceMinor(r),
      parsedQty(r),
      r.vat_rate_basis_points,
    );
    netTotal += lt.net;
    vatTotal += lt.vat;
    return lt;
  });
  const grossTotal = netTotal + vatTotal;

  return (
    <div className="flex flex-col gap-4">
      <Table>
        <THead>
          <TR>
            <TH>Variant</TH>
            <TH className="text-right">Qty</TH>
            <TH className="text-right">Requested</TH>
            <TH className="text-right">Quoted</TH>
            <TH>VAT</TH>
            <TH className="text-right">Gross</TH>
            {!disabled ? <TH>{/* remove */}</TH> : null}
          </TR>
        </THead>
        <TBody>
          {rows.map((row, idx) => (
            <TR key={`row-${idx}-${row.variant_id || "new"}`}>
              <TD>
                {row.product_title ? (
                  // Resolved line (existing item): show product name + variant
                  // + sku with a catalog deep-link, in BOTH the read-only and
                  // the editable (requested/revised) views — the admin reviews
                  // by product, not by UUID. The variant_id is fixed for an
                  // existing line, so no input is needed here.
                  <div className="flex flex-col gap-0.5">
                    {row.product_id ? (
                      <a
                        href={`/products/${row.product_id}`}
                        className="text-body-sm font-medium text-accent hover:underline"
                      >
                        {row.product_title}
                      </a>
                    ) : (
                      <span className="text-body-sm font-medium text-ink-900">
                        {row.product_title}
                      </span>
                    )}
                    <span className="text-caption text-ink-500">
                      {row.variant_title}
                      {row.sku ? ` · ${row.sku}` : ""}
                    </span>
                  </div>
                ) : disabled ? (
                  // Read-only line whose variant could not be resolved
                  // (deleted/archived) — fall back to the raw id.
                  <span className="font-mono text-caption text-ink-500">
                    {row.variant_id || "—"}
                  </span>
                ) : (
                  // Brand-new row the admin is adding — type the variant UUID.
                  <Input
                    aria-label={`Variant ID for row ${idx + 1}`}
                    type="text"
                    value={row.variant_id}
                    placeholder="Variant UUID"
                    onChange={(e) =>
                      updateRow(idx, { variant_id: e.target.value })
                    }
                  />
                )}
              </TD>
              <TD className="text-right">
                {disabled ? (
                  <Numeric>{row.quantity}</Numeric>
                ) : (
                  <Input
                    aria-label={`Quantity for row ${idx + 1}`}
                    type="text"
                    inputMode="numeric"
                    value={row.quantity}
                    onChange={(e) =>
                      updateRow(idx, { quantity: e.target.value })
                    }
                    className="text-right"
                  />
                )}
              </TD>
              <TD className="text-right text-body-sm text-ink-700">
                <Numeric>
                  {formatGBP(row.requested_unit_price_minor)}
                </Numeric>
              </TD>
              <TD className="text-right">
                {disabled ? (
                  <Numeric>
                    {row.quoted_unit_price_minor === "" ||
                    row.quoted_unit_price_minor === null
                      ? "—"
                      : formatGBP(Number(row.quoted_unit_price_minor))}
                  </Numeric>
                ) : (
                  <Input
                    aria-label={`Quoted unit price (pence) for row ${
                      idx + 1
                    }`}
                    type="text"
                    inputMode="numeric"
                    placeholder="use system"
                    value={row.quoted_unit_price_minor}
                    onChange={(e) =>
                      updateRow(idx, {
                        quoted_unit_price_minor: e.target.value,
                      })
                    }
                    className="text-right"
                  />
                )}
              </TD>
              <TD className="text-body-sm text-ink-500">
                {row.vat_category_code
                  ? `${row.vat_category_code} (${formatVatRate(
                      row.vat_rate_basis_points,
                    )})`
                  : "—"}
              </TD>
              <TD className="text-right">
                <Numeric serif>
                  {formatGBP(rowTotals[idx]?.gross ?? 0)}
                </Numeric>
              </TD>
              {!disabled ? (
                <TD>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeRow(idx)}
                    aria-label={`Remove row ${idx + 1}`}
                  >
                    Remove
                  </Button>
                </TD>
              ) : null}
            </TR>
          ))}
        </TBody>
      </Table>

      {!disabled ? (
        <div>
          <Button variant="secondary" size="sm" onClick={addRow}>
            Add line
          </Button>
        </div>
      ) : null}

      {/* Live totals strip */}
      <dl className="flex flex-col gap-1 self-end min-w-[14rem] text-body-sm">
        <div className="flex justify-between gap-6">
          <dt className="text-ink-500">Net</dt>
          <dd>
            <Numeric>{formatGBP(netTotal)}</Numeric>
          </dd>
        </div>
        <div className="flex justify-between gap-6">
          <dt className="text-ink-500">VAT</dt>
          <dd>
            <Numeric>{formatGBP(vatTotal)}</Numeric>
          </dd>
        </div>
        <div className="flex justify-between gap-6 font-medium text-ink-900">
          <dt>Gross</dt>
          <dd>
            <Numeric serif>{formatGBP(grossTotal)}</Numeric>
          </dd>
        </div>
      </dl>
    </div>
  );
}
